/**
 * IPDReferralsInboxPage — list of OPD → IPD referrals.
 *
 * Doctor: sees their OWN referrals
 * Billing / Admin / FD: sees all + can click "Admit Now"
 * Nurse: read-only view
 *
 * Route: /ipd-referrals
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ipdReferralsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowRightCircle, AlertTriangle, Clock, BedDouble, Stethoscope, User as UserIcon, X, Hourglass } from 'lucide-react';

const URGENCY_BADGE = {
  routine:   { label: 'Routine',   bg: 'bg-slate-100 text-slate-700' },
  urgent:    { label: 'Urgent',    bg: 'bg-amber-100 text-amber-700' },
  emergency: { label: 'EMERGENCY', bg: 'bg-rose-100 text-rose-700 animate-pulse' },
};

const STATUS_BADGE = {
  pending:   { label: 'Pending',   bg: 'bg-blue-100 text-blue-700' },
  admitted:  { label: 'Admitted',  bg: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-slate-100 text-slate-500' },
};

const IPDReferralsInboxPage = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');

  const canAction = hasPermission ? hasPermission('ipd_referrals.manage') : false;
  const isDoctor = (user?.role_code || '').toLowerCase() === 'doctor';

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await ipdReferralsAPI.list(params);
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load referrals');
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.patient_name || '').toLowerCase().includes(q)
      || (r.patient_uhid || '').toLowerCase().includes(q)
      || (r.referred_by_doctor_name || '').toLowerCase().includes(q)
      || (r.clinical_reason || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const admitNow = (r) => {
    // Send user to Admissions page with referral pre-filled via query string
    navigate(`/admissions?referral=${r.id}&patient=${r.patient_id}`);
  };

  const cancelOne = async (r) => {
    const reason = window.prompt('Reason for cancelling this referral?');
    if (!reason) return;
    try {
      await ipdReferralsAPI.cancel(r.id, reason);
      toast.success('Referral cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  return (
    <div className="space-y-5" data-testid="ipd-referrals-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white shadow-md">
            <ArrowRightCircle size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">IPD Referrals Inbox</h1>
            <p className="text-sm text-slate-500">Patients referred from OPD/consult for IPD admission. {isDoctor && '(Showing your referrals only.)'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="referrals-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="admitted">Admitted</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Search patient / doctor / reason" value={search} onChange={e => setSearch(e.target.value)} className="w-72" data-testid="referrals-search" />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{filtered.length} referral{filtered.length !== 1 ? 's' : ''}</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-slate-400 text-sm">Loading…</div> :
            filtered.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">No referrals match.</div> :
            <div className="divide-y divide-slate-100">
              {filtered.map(r => {
                const urg = URGENCY_BADGE[r.urgency] || URGENCY_BADGE.routine;
                const st = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                return (
                  <div key={r.id} className="p-4 hover:bg-sky-50/40 transition" data-testid={`referral-row-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-bold text-slate-900 inline-flex items-center gap-1"><UserIcon size={13} /> {r.patient_name}</h3>
                          {r.patient_uhid && <span className="font-mono text-[10px] text-slate-500">{r.patient_uhid}</span>}
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${urg.bg}`}>{r.urgency === 'emergency' && <AlertTriangle size={10} className="inline mr-0.5" />}{urg.label}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${st.bg}`}>{st.label}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold inline-flex items-center gap-1"><BedDouble size={10} /> {r.suggested_admission_type === 'day_care' ? 'Day Care' : 'Inpatient'} · {r.suggested_ward_type}</span>
                        </div>
                        <p className="text-xs text-slate-700 mt-1.5"><strong>Reason: </strong>{r.clinical_reason}</p>
                        {r.diagnosis_summary && <p className="text-xs text-slate-600 mt-0.5"><strong>Diagnosis: </strong>{r.diagnosis_summary}</p>}
                        {r.notes && <p className="text-[11px] text-slate-500 italic mt-1">{r.notes}</p>}
                        <div className="mt-2 flex items-center gap-3 flex-wrap text-[10px] text-slate-500">
                          <span className="inline-flex items-center gap-1"><Stethoscope size={10} /> {r.referred_by_doctor_name}</span>
                          <span className="inline-flex items-center gap-1"><Clock size={10} /> {new Date(r.referred_at).toLocaleString('en-IN')}</span>
                          {r.status === 'admitted' && r.admitted_at && <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><Hourglass size={10} /> Admitted {new Date(r.admitted_at).toLocaleString('en-IN')}</span>}
                          {r.status === 'cancelled' && r.cancelled_reason && <span className="text-rose-500">Cancelled: {r.cancelled_reason}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.status === 'pending' && canAction && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => admitNow(r)} data-testid={`referral-admit-${r.id}`}>
                            <ArrowRightCircle size={14} className="mr-1.5" /> Admit Now
                          </Button>
                        )}
                        {r.status === 'pending' && (isDoctor || canAction) && (
                          <Button size="sm" variant="ghost" onClick={() => cancelOne(r)} data-testid={`referral-cancel-${r.id}`}>
                            <X size={14} className="mr-1 text-slate-500" /> Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
};

export default IPDReferralsInboxPage;
